import { formatJobNumber } from '@fieldops/shared'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { AddressField, addressIsUsable, emptyAddress, type AddressDraft } from '../../components/AddressField.js'
import { DialogBody, DialogFooter, DialogHeader, Modal } from '../../components/Modal.js'
import { Avatar, Button, Field, Input, Select, Textarea } from '../../components/ui.js'
import { useTRPC } from '../../lib/trpc.js'
import type { CalendarEventType, SchedulablePerson } from './lib/types.js'
import { dateInputValue } from '../../lib/format.js'

/**
 * Adding booked time that is not a job, or blanking a row.
 *
 * Task / Break / Meeting write a CalendarEvent. Unavailable writes StaffLeave
 * with clock times — that is how a director hides an afternoon without
 * inventing a job. Multi-day annual leave stays on Add leave. Decision 67.
 */

export type AddToDayKind = 'TASK' | 'BREAK' | 'MEETING' | 'UNAVAILABLE'

const KINDS: Array<{ value: AddToDayKind; label: string }> = [
  { value: 'TASK', label: 'Task' },
  { value: 'BREAK', label: 'Break' },
  { value: 'MEETING', label: 'Meeting' },
  { value: 'UNAVAILABLE', label: 'Unavailable' },
]

const EVENT_TYPES: Array<{ value: CalendarEventType; label: string }> = [
  { value: 'MEETING', label: 'Meeting' },
  { value: 'TRAINING', label: 'Training' },
  { value: 'ADMIN', label: 'Admin time' },
  { value: 'OTHER', label: 'Other' },
]

export type AddToDayInput =
  | {
      kind: 'event'
      title: string
      type: CalendarEventType
      startAt: Date
      endAt: Date
      note: string | null
      phone: string | null
      jobId: string | null
      address: AddressDraft | null
      userIds: string[]
    }
  | {
      kind: 'unavailable'
      userId: string
      startAt: Date
      endAt: Date
      note: string | null
    }

function clockInput(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

/**
 * Where the clock starts when the form was opened from a row rather than from
 * a drawn slot. A break defaults to the middle of the day at half an hour,
 * because that is the shape of nearly every one of them; an afternoon somebody
 * blanks out is 1–4.
 */
const DEFAULT_CLOCK: Record<AddToDayKind, { from: string; to: string }> = {
  TASK: { from: '09:00', to: '10:00' },
  BREAK: { from: '12:00', to: '12:30' },
  MEETING: { from: '09:00', to: '10:00' },
  UNAVAILABLE: { from: '13:00', to: '16:00' },
}

export function AddEventPopup({
  open,
  person,
  people,
  defaultDate,
  defaultStartAt,
  defaultEndAt,
  defaultType = 'MEETING',
  defaultJobId = null,
  isPending,
  error,
  onClose,
  onSave,
}: {
  open: boolean
  person: SchedulablePerson | null
  people: SchedulablePerson[]
  defaultDate: Date
  defaultStartAt?: Date
  defaultEndAt?: Date
  defaultType?: AddToDayKind
  defaultJobId?: string | null
  isPending: boolean
  error: string | null
  onClose: () => void
  onSave: (input: AddToDayInput) => void
}) {
  const trpc = useTRPC()
  const [kind, setKind] = useState<AddToDayKind>(defaultType)
  const [title, setTitle] = useState(defaultType === 'BREAK' ? 'Break' : '')
  const [meetingType, setMeetingType] = useState<CalendarEventType>('MEETING')
  const [whoId, setWhoId] = useState(person?.id ?? '')
  const [date, setDate] = useState(() => dateInputValue(defaultStartAt ?? defaultDate))
  const [startTime, setStartTime] = useState(() =>
    defaultStartAt ? clockInput(defaultStartAt) : DEFAULT_CLOCK[defaultType].from,
  )
  const [endTime, setEndTime] = useState(() =>
    defaultEndAt ? clockInput(defaultEndAt) : DEFAULT_CLOCK[defaultType].to,
  )
  const [note, setNote] = useState('')
  const [phone, setPhone] = useState('')
  const [address, setAddress] = useState<AddressDraft>(emptyAddress)
  const [jobId, setJobId] = useState<string | null>(defaultJobId)
  const [jobQuery, setJobQuery] = useState('')
  const [alsoAttending, setAlsoAttending] = useState<Set<string>>(new Set())

  const jobs = useQuery({
    ...trpc.jobs.list.queryOptions({ q: jobQuery || undefined, perPage: 8, tab: 'WORK_ORDER' }),
    enabled: kind === 'TASK' && jobQuery.trim().length >= 2,
  })

  const who = person ?? people.find((entry) => entry.id === whoId) ?? null
  const invalid = endTime <= startTime || !who
  const others = people.filter((entry) => entry.id !== who?.id)
  const isEvent = kind !== 'UNAVAILABLE'

  const reset = () => {
    setKind(defaultType)
    setTitle(defaultType === 'BREAK' ? 'Break' : '')
    setMeetingType('MEETING')
    setWhoId(person?.id ?? '')
    setDate(dateInputValue(defaultStartAt ?? defaultDate))
    setStartTime(defaultStartAt ? clockInput(defaultStartAt) : DEFAULT_CLOCK[defaultType].from)
    setEndTime(defaultEndAt ? clockInput(defaultEndAt) : DEFAULT_CLOCK[defaultType].to)
    setNote('')
    setPhone('')
    setAddress(emptyAddress())
    setJobId(defaultJobId)
    setJobQuery('')
    setAlsoAttending(new Set())
  }

  const close = () => {
    reset()
    onClose()
  }

  const heading =
    kind === 'UNAVAILABLE'
      ? `Unavailable — ${who?.name ?? ''}`
      : kind === 'TASK'
        ? `Task for ${who?.name ?? '…'}`
        : kind === 'BREAK'
          ? `Break for ${who?.name ?? '…'}`
          : `Meeting for ${who?.name ?? '…'}`

  return (
    <Modal open={open} onClose={close} labelledBy="add-event-title" className="max-w-sm">
      {open ? (
        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(event) => {
            event.preventDefault()
            if (invalid) return

            const startAt = new Date(`${date}T${startTime}:00`)
            const endAt = new Date(`${date}T${endTime}:00`)

            if (!who) return

            if (kind === 'UNAVAILABLE') {
              onSave({
                kind: 'unavailable',
                userId: who.id,
                startAt,
                endAt,
                note: note.trim() || null,
              })
              return
            }

            const type: CalendarEventType =
              kind === 'TASK' ? 'TASK' : kind === 'BREAK' ? 'BREAK' : meetingType
            const resolvedTitle =
              title.trim() || (kind === 'BREAK' ? 'Break' : kind === 'TASK' ? 'Task' : 'Meeting')

            onSave({
              kind: 'event',
              title: resolvedTitle,
              type,
              startAt,
              endAt,
              note: note.trim() || null,
              phone: kind === 'TASK' ? phone.trim() || null : null,
              jobId: kind === 'TASK' ? jobId : null,
              address: kind === 'TASK' && addressIsUsable(address) ? address : null,
              userIds: [who.id, ...alsoAttending],
            })
          }}
        >
          <DialogHeader
            id="add-event-title"
            title={heading}
            lead={who ? <Avatar name={who.name} colour={who.colour} size="sm" /> : undefined}
            onClose={close}
          />

          <DialogBody className="p-4">
            {person ? null : (
              <Field label="Who" htmlFor="event-who">
                <Select
                  id="event-who"
                  value={whoId}
                  onChange={(event) => setWhoId(event.target.value)}
                >
                  <option value="">Pick someone</option>
                  {people.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.name}
                    </option>
                  ))}
                </Select>
              </Field>
            )}

            <Field label="What is it" htmlFor="event-kind">
              <Select
                id="event-kind"
                value={kind}
                onChange={(event) => {
                  const next = event.target.value as AddToDayKind
                  setKind(next)
                  if (next === 'BREAK' && title.trim() === '') setTitle('Break')
                }}
              >
                {KINDS.map((entry) => (
                  <option key={entry.value} value={entry.value}>
                    {entry.label}
                  </option>
                ))}
              </Select>
            </Field>

            {isEvent && kind === 'MEETING' ? (
              <Field label="Type" htmlFor="event-type">
                <Select
                  id="event-type"
                  value={meetingType}
                  onChange={(event) => setMeetingType(event.target.value as CalendarEventType)}
                >
                  {EVENT_TYPES.map((entry) => (
                    <option key={entry.value} value={entry.value}>
                      {entry.label}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null}

            {isEvent ? (
              <Field label="Title" htmlFor="event-title">
                <Input
                  id="event-title"
                  required={kind !== 'BREAK'}
                  maxLength={200}
                  placeholder={
                    kind === 'TASK'
                      ? 'Pick up keys'
                      : kind === 'BREAK'
                        ? 'Break'
                        : 'Monday toolbox talk'
                  }
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                />
              </Field>
            ) : null}

            <Field label="Day" htmlFor="event-date">
              <input
                id="event-date"
                type="date"
                required
                value={date}
                onChange={(event) => setDate(event.target.value)}
                className="block w-full rounded-md border-0 bg-white px-2.5 text-sm text-ink-900 ring-1 ring-inset ring-ink-300 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-600 min-h-[var(--control-md)]"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="From" htmlFor="event-start">
                <input
                  id="event-start"
                  type="time"
                  required
                  step={900}
                  value={startTime}
                  onChange={(event) => setStartTime(event.target.value)}
                  className="block w-full rounded-md border-0 bg-white px-2.5 text-sm text-ink-900 ring-1 ring-inset ring-ink-300 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-600 min-h-[var(--control-md)]"
                />
              </Field>
              <Field
                label="To"
                htmlFor="event-end"
                error={invalid ? 'Ends before it starts.' : undefined}
              >
                <input
                  id="event-end"
                  type="time"
                  required
                  step={900}
                  value={endTime}
                  onChange={(event) => setEndTime(event.target.value)}
                  className="block w-full rounded-md border-0 bg-white px-2.5 text-sm text-ink-900 ring-1 ring-inset ring-ink-300 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-600 min-h-[var(--control-md)]"
                />
              </Field>
            </div>

            {kind === 'TASK' ? (
              <>
                <AddressField id="event-address" value={address} onChange={setAddress} />
                <Field label="Phone" htmlFor="event-phone" hint="Optional — shown on the phone.">
                  <Input
                    id="event-phone"
                    type="tel"
                    maxLength={40}
                    placeholder="0412 000 000"
                    value={phone}
                    onChange={(event) => setPhone(event.target.value)}
                  />
                </Field>
                <Field label="Linked job" htmlFor="event-job" hint="Optional — collect keys for this job.">
                  <Input
                    id="event-job"
                    value={jobQuery}
                    placeholder="Search by number or client"
                    onChange={(event) => {
                      setJobQuery(event.target.value)
                      setJobId(null)
                    }}
                  />
                  {jobId ? (
                    <p className="mt-1 text-xs text-ink-600">Linked.</p>
                  ) : jobQuery.trim().length >= 2 ? (
                    <ul className="mt-1 max-h-32 overflow-y-auto rounded-md ring-1 ring-inset ring-ink-200">
                      {(jobs.data?.items ?? []).map((job) => (
                        <li key={job.id}>
                          <button
                            type="button"
                            className="flex w-full flex-col items-start px-2.5 py-1.5 text-left text-sm hover:bg-ink-50"
                            onClick={() => {
                              setJobId(job.id)
                              setJobQuery(
                                `${formatJobNumber(job)} · ${job.client.name}`,
                              )
                            }}
                          >
                            <span className="font-medium text-ink-900">
                              {formatJobNumber(job)} · {job.client.name}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </Field>
              </>
            ) : null}

            {isEvent && others.length > 0 ? (
              <Field
                label="Who else"
                hint="Draws on their row too, and comes off their free time."
              >
                <div className="max-h-40 overflow-y-auto rounded-md ring-1 ring-inset ring-ink-300">
                  {others.map((entry) => (
                    <label
                      key={entry.id}
                      className="flex cursor-pointer items-center gap-2 px-2.5 py-1.5 text-sm text-ink-800 hover:bg-ink-50"
                    >
                      <input
                        type="checkbox"
                        checked={alsoAttending.has(entry.id)}
                        onChange={() =>
                          setAlsoAttending((current) => {
                            const next = new Set(current)
                            if (next.has(entry.id)) next.delete(entry.id)
                            else next.add(entry.id)
                            return next
                          })
                        }
                      />
                      <span className="truncate">{entry.name}</span>
                    </label>
                  ))}
                </div>
              </Field>
            ) : null}

            <Field
              label="Note"
              htmlFor="event-note"
              hint={kind === 'UNAVAILABLE' ? 'Optional — shown on the hatch.' : 'Optional — shown on the board.'}
            >
              <Textarea
                id="event-note"
                maxLength={500}
                placeholder={kind === 'UNAVAILABLE' ? 'Board meeting off-site' : 'Bring the new SDS folder'}
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            </Field>

            {error ? (
              <p className="text-xs text-red-600" role="alert">
                {error}
              </p>
            ) : null}
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isPending} disabled={invalid}>
              {kind === 'UNAVAILABLE' ? 'Mark unavailable' : kind === 'TASK' ? 'Add task' : kind === 'BREAK' ? 'Add break' : 'Add meeting'}
            </Button>
          </DialogFooter>
        </form>
      ) : null}
    </Modal>
  )
}
